import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { Coins, Tag, LogIn, CheckCircle, AlertTriangle, Clock, ArrowLeft, Sparkles, Wallet, Copy } from "lucide-react";
import { L, t } from "../lib/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Confetti ── */
function ConfettiParticle({ style }) {
  return <div className="confetti-particle" style={style} />;
}

function Confetti({ active }) {
  const [particles, setParticles] = useState([]);
  useEffect(() => {
    if (!active) { setParticles([]); return; }
    const colors = ["#8B5CF6","#F77737","#E1306C","#22c55e","#4F46E5","#facc15","#06b6d4"];
    const shapes = ["circle","square","diamond"];
    const ps = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      color: colors[i % colors.length],
      shape: shapes[i % shapes.length],
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 0.6}s`,
      duration: `${0.9 + Math.random() * 0.8}s`,
      size: `${6 + Math.random() * 8}px`,
      tx: `${(Math.random() - 0.5) * 300}px`,
    }));
    setParticles(ps);
    const t = setTimeout(() => setParticles([]), 2200);
    return () => clearTimeout(t);
  }, [active]);

  if (!particles.length) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      {particles.map(p => (
        <ConfettiParticle key={p.id} style={{
          position: "absolute",
          top: "40%",
          left: p.left,
          width: p.size,
          height: p.size,
          background: p.color,
          borderRadius: p.shape === "circle" ? "50%" : p.shape === "diamond" ? "0" : "2px",
          transform: p.shape === "diamond" ? "rotate(45deg)" : "none",
          animation: `confettiBurst ${p.duration} ${p.delay} ease-out forwards`,
          "--tx": p.tx,
        }} />
      ))}
    </div>
  );
}

/* ── Floating coins animation ── */
function FloatingCoins() {
  return (
    <div className="relative w-24 h-24 mx-auto">
      {[0,1,2].map(i => (
        <div key={i} className="absolute inset-0 flex items-center justify-center"
          style={{ animation: `coinFloat 2s ${i * 0.3}s ease-in-out infinite`, opacity: 1 - i * 0.25 }}>
          <div className="flex items-center justify-center rounded-full text-3xl"
            style={{ width: 64 - i*10, height: 64 - i*10, background: `rgba(247,119,55,${0.25 - i*0.06})`, border: `2px solid rgba(247,119,55,${0.5 - i*0.12})` }}>
            {i === 0 && "🪙"}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Discount badge animation ── */
function DiscountBadge({ value }) {
  return (
    <div className="relative w-28 h-28 mx-auto flex items-center justify-center">
      <div style={{ animation: "badgePulse 2s ease-in-out infinite" }}>
        <div className="w-24 h-24 rounded-full flex flex-col items-center justify-center"
          style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)", boxShadow: "0 0 32px rgba(139,92,246,0.4)" }}>
          <span className="text-white font-black text-2xl font-outfit leading-none">+{value}%</span>
          <span className="text-white/70 text-[10px] font-semibold tracking-wide mt-0.5">BONUS</span>
        </div>
      </div>
    </div>
  );
}

/* ── Status: not found / expired / exhausted ── */
function ErrorState({ status, lang, code }) {
  const cfg = {
    not_found:  { icon: "🔍", title: t("Coupon.coupon_not_found", lang),        desc: t("Coupon.this_coupon_code_doesn_t_exist_or_has_been_remov", lang), color: "#ef4444" },
    expired:    { icon: "⌛", title: t("Coupon.coupon_expired", lang),              desc: t("Coupon.this_coupon_has_expired_and_is_no_longer_valid", lang), color: "#facc15" },
    exhausted:  { icon: "💯", title: t("Coupon.coupon_used_up", lang),             desc: t("Coupon.this_coupon_has_reached_its_usage_limit", lang), color: "#F77737" },
    already_used: { icon: "✅", title: t("Coupon.already_activated", lang),       desc: t("Coupon.you_ve_already_used_this_coupon", lang), color: "#22c55e" },
  }[status] || { icon: "❓", title: "Unknown", desc: "", color: "#9B9BB0" };

  return (
    <div className="text-center py-4">
      <div className="text-5xl mb-4" style={{ animation: "fadeInUp 0.5s ease-out" }}>{cfg.icon}</div>
      <h2 className="text-xl font-black mb-2 font-outfit" style={{ color: cfg.color }}>{cfg.title}</h2>
      <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>{cfg.desc}</p>
      <Link to="/dashboard" className="inline-flex items-center gap-2 h-10 px-5 rounded-xl font-semibold text-sm"
        style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
        <ArrowLeft className="w-4 h-4" />
        {t("Coupon.go_to_dashboard", lang)}
      </Link>
    </div>
  );
}

export default function CouponPage() {
  const { code } = useParams();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoActivate = searchParams.get("activate") === "1";
  const didAutoActivate = useRef(false);

  const [promo, setPromo] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [activating, setActivating] = useState(false);
  const [success, setSuccess] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!code) { setPageStatus("not_found"); return; }
    try {
      const r = await fetch(`${API}/promo/preview/${encodeURIComponent(code.toUpperCase())}`, { credentials: "include" });
      const d = await r.json();
      setPromo(d);
      setPageStatus(d.status || "not_found");
    } catch {
      setPageStatus("not_found");
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  // Save pending coupon to localStorage as soon as the page is visited (even before clicking login)
  useEffect(() => {
    if (code && !user) {
      localStorage.setItem("pending_coupon", code.toUpperCase());
    }
  }, [code, user]);

  const activate = useCallback(async (promoData) => {
    const p = promoData || promo;
    if (!user) {
      localStorage.setItem("pending_coupon", code.toUpperCase());
      navigate(`/login?next=${encodeURIComponent(`/coupon/${code}?activate=1`)}`);
      return;
    }
    if (p?.bonus_type === "percentage") {
      setSuccess({ type: "percentage", bonus_pct: p.bonus_value, code: code.toUpperCase() });
      setPageStatus("success");
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2500);
      setTimeout(() => navigate(`/topup?promo=${encodeURIComponent(code.toUpperCase())}`), 2800);
      return;
    }
    setActivating(true);
    try {
      const r = await fetch(`${API}/promo-codes/apply`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.toUpperCase() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setSuccess(d);
      setPageStatus("success");
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2500);
    } catch (e) {
      toast.error(e.message);
      setActivating(false);
    } finally {
      setActivating(false);
    }
  }, [user, promo, code, navigate]);

  // Auto-activate after login redirect
  useEffect(() => {
    if (!autoActivate || !user || !promo || didAutoActivate.current) return;
    if (promo.status === "valid") {
      didAutoActivate.current = true;
      activate(promo);
    }
  }, [autoActivate, user, promo, activate]);

  const copyCode = () => {
    navigator.clipboard.writeText(code.toUpperCase()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const isFixed = promo?.bonus_type === "fixed";
  const isPercent = promo?.bonus_type === "percentage";
  const desc = promo?.description?.[lang] || promo?.description?.en || promo?.description?.ru || promo?.description?.he || "";

  const themeColor = isFixed ? "#F77737" : "#8B5CF6";
  const themeGlow = isFixed ? "rgba(247,119,55,0.25)" : "rgba(139,92,246,0.25)";
  const themeBg = isFixed ? "rgba(247,119,55,0.08)" : "rgba(139,92,246,0.08)";
  const themeBorder = isFixed ? "rgba(247,119,55,0.25)" : "rgba(139,92,246,0.25)";

  return (
    <>
      <style>{`
        @keyframes coinFloat {
          0%,100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-10px) scale(1.05); }
        }
        @keyframes badgePulse {
          0%,100% { transform: scale(1); filter: drop-shadow(0 0 8px rgba(139,92,246,0.4)); }
          50% { transform: scale(1.06); filter: drop-shadow(0 0 20px rgba(139,92,246,0.7)); }
        }
        @keyframes fadeInUp {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes cardEntrance {
          from { opacity:0; transform:translateY(32px) scale(0.96); }
          to   { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        @keyframes successPop {
          0%   { opacity:0; transform:scale(0.5); }
          60%  { transform:scale(1.15); }
          100% { opacity:1; transform:scale(1); }
        }
        @keyframes confettiBurst {
          0%   { opacity:1; transform:translate(0,0) rotate(0deg); }
          100% { opacity:0; transform:translate(var(--tx,0),-280px) rotate(720deg); }
        }
        .coupon-card {
          animation: cardEntrance 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        .coupon-shimmer {
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%);
          background-size: 400px 100%;
          animation: shimmer 2.5s infinite linear;
        }
        .confetti-particle { position:absolute; }
        @keyframes chatDot {
          0%,80%,100% { transform:scale(0.7); opacity:0.4; }
          40%          { transform:scale(1.2); opacity:1; }
        }
      `}</style>

      <Confetti active={showConfetti} />

      <div className="min-h-screen pt-14 pb-20 flex items-start justify-center"
        style={{ background: "var(--bg-primary)" }}>
        <div className="w-full max-w-md mx-auto px-4 py-8">

          {/* Back link */}
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs font-medium mb-6 transition-opacity hover:opacity-70"
            style={{ color: "var(--text-secondary)" }}>
            <ArrowLeft className="w-3.5 h-3.5" />
            {t("Coupon.dashboard", lang)}
          </Link>

          {/* ── Loading ── */}
          {pageStatus === "loading" && (
            <div className="rounded-2xl border p-8" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
              <div className="flex flex-col items-center gap-4">
                <div className="w-20 h-20 rounded-full" style={{ background: "var(--surface-2)", animation: "shimmer 1.5s infinite" }} />
                <div className="h-4 w-32 rounded-full" style={{ background: "var(--surface-2)" }} />
                <div className="h-3 w-48 rounded-full" style={{ background: "var(--surface-2)" }} />
              </div>
            </div>
          )}

          {/* ── Error states ── */}
          {["not_found","expired","exhausted"].includes(pageStatus) && (
            <div className="rounded-2xl border p-8" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
              <ErrorState status={pageStatus} lang={lang} code={code} />
            </div>
          )}

          {/* ── Already used ── */}
          {pageStatus === "already_used" && (
            <div className="coupon-card rounded-2xl border p-8" style={{ borderColor: "rgba(34,197,94,0.25)", background: "rgba(34,197,94,0.05)" }}>
              <ErrorState status="already_used" lang={lang} code={code} />
            </div>
          )}

          {/* ── Valid / Not logged in ── */}
          {(pageStatus === "valid") && promo && (
            <div className="coupon-card rounded-2xl border overflow-hidden"
              style={{ borderColor: themeBorder, background: "var(--bg-secondary)", boxShadow: `0 8px 48px ${themeGlow}` }}>

              {/* Shimmer overlay */}
              <div className="coupon-shimmer absolute inset-0 pointer-events-none rounded-2xl" style={{ position:"relative" }} />

              {/* Header band */}
              <div className="px-5 pt-5 pb-3 text-center relative" style={{ borderBottom: `1px solid ${themeBorder}`, background: themeBg }}>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: themeColor }}>
                  {isFixed ? t("Coupon.ipoints_coupon", lang) : t("Coupon.top_up_bonus_coupon", lang)}
                </div>

                {/* Animated icon */}
                <div className="mb-3">
                  {isFixed ? <FloatingCoins /> : <DiscountBadge value={promo.bonus_value} />}
                </div>

                {/* Value */}
                <div className="text-5xl font-black font-outfit mb-1" style={{ color: themeColor,
                  textShadow: `0 0 32px ${themeGlow}` }}>
                  {isFixed ? `+${Number(promo.bonus_value).toLocaleString()}` : `+${promo.bonus_value}%`}
                </div>
                <div className="text-sm font-semibold mb-1" style={{ color: "var(--text-secondary)" }}>
                  {isFixed ? "iPoints" : t("Coupon.bonus_on_top_up", lang)}
                </div>
              </div>

              {/* Body */}
              <div className="px-5 py-4 space-y-4">
                {/* Code badge */}
                <button onClick={copyCode}
                  className="w-full flex items-center justify-between gap-2 rounded-xl px-4 py-3 transition-all hover:opacity-80 group"
                  style={{ background: themeBg, border: `1px solid ${themeBorder}` }}>
                  <span className="text-lg font-black tracking-[0.15em] font-outfit" style={{ color: themeColor }}>
                    {code?.toUpperCase()}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-semibold flex-shrink-0" style={{ color: themeColor }}>
                    {copied ? <><CheckCircle className="w-3.5 h-3.5" />{t("Coupon.copied", lang)}</> : <><Copy className="w-3.5 h-3.5" />{t("DashboardV2.copy", lang)}</>}
                  </span>
                </button>

                {/* Description */}
                {desc && (
                  <p className="text-sm text-center leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {desc}
                  </p>
                )}

                {/* Meta row */}
                <div className="flex items-center justify-center gap-4 text-xs" style={{ color: "var(--text-secondary)" }}>
                  {promo.expires_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {t("Coupon.expires", lang)}: {new Date(promo.expires_at).toLocaleDateString(lang === "he" ? "he-IL" : lang === "ru" ? "ru-RU" : "en-US")}
                    </span>
                  )}
                  {promo.uses_left != null && (
                    <span className="flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      {promo.uses_left} {t("Coupon.left", lang)}
                    </span>
                  )}
                </div>

                {/* CTA */}
                {!user ? (
                  <div className="space-y-2">
                    <div className="rounded-xl px-4 py-3 text-center text-sm" style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.2)", color: "var(--text-secondary)" }}>
                      {t("Coupon.sign_in_or_create_an_account_to_activate_this_co", lang)}
                    </div>
                    <Link to={`/login?next=${encodeURIComponent(`/coupon/${code}?activate=1`)}`}
                      onClick={() => localStorage.setItem("pending_coupon", code?.toUpperCase())}
                      className="w-full h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
                      style={{ background: `linear-gradient(135deg, ${themeColor}, ${isFixed ? "#E1306C" : "#4F46E5"})`, color: "#fff" }}>
                      <LogIn className="w-4 h-4" />
                      {t("Coupon.login_register", lang)}
                    </Link>
                  </div>
                ) : (
                  <button onClick={() => activate()} disabled={activating}
                    className="w-full h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60"
                    style={{ background: `linear-gradient(135deg, ${themeColor}, ${isFixed ? "#E1306C" : "#4F46E5"})`, color: "#fff" }}>
                    {activating ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="40" strokeLinecap="round"/>
                        </svg>
                        {t("Coupon.activating", lang)}
                      </span>
                    ) : isFixed ? (
                      <><Coins className="w-4 h-4" />{t("Coupon.activate_get_ipoints", lang)}</>
                    ) : (
                      <><Wallet className="w-4 h-4" />{t("Coupon.use_at_top_up", lang)}</>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Success ── */}
          {pageStatus === "success" && success && (
            <div className="coupon-card rounded-2xl border overflow-hidden text-center"
              style={{ borderColor: "rgba(34,197,94,0.3)", background: "var(--bg-secondary)", boxShadow: "0 8px 48px rgba(34,197,94,0.2)" }}>
              <div className="px-5 pt-8 pb-6">
                <div style={{ animation: "successPop 0.6s cubic-bezier(0.34,1.56,0.64,1) both" }}>
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: "rgba(34,197,94,0.12)", border: "2px solid rgba(34,197,94,0.3)" }}>
                    <CheckCircle className="w-10 h-10" style={{ color: "#22c55e" }} />
                  </div>
                </div>

                <h2 className="text-2xl font-black font-outfit mb-2" style={{ color: "#22c55e" }}>
                  {t("Coupon.coupon_activated", lang)}
                </h2>

                {success.type === "fixed" && (
                  <>
                    <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
                      {t("Coupon.credited_to_your_balance", lang)}
                    </p>
                    <div className="text-4xl font-black font-outfit mb-5" style={{ color: "#F77737" }}>
                      +{Number(success.bonus).toLocaleString()} <span className="text-2xl">iP</span>
                    </div>
                  </>
                )}

                {success.type === "percentage" && (
                  <>
                    <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
                      {t("Coupon.bonus_applied_to_your_next_top_up", lang)}
                    </p>
                    <div className="text-4xl font-black font-outfit mb-2" style={{ color: "#8B5CF6" }}>
                      +{success.bonus_pct}%
                    </div>
                    <p className="text-xs mb-4" style={{ color: "var(--text-secondary)" }}>
                      {t("Coupon.redirecting_to_top_up", lang)}
                    </p>
                    <div className="flex justify-center mb-2">
                      <div className="flex gap-1">
                        {[0,1,2].map(i => (
                          <div key={i} className="w-2 h-2 rounded-full" style={{
                            background: "#8B5CF6",
                            animation: `chatDot 1.2s ${i*0.2}s ease-in-out infinite`,
                          }} />
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {success.type !== "percentage" && (
                  <div className="flex gap-3 justify-center">
                    <Link to="/dashboard"
                      className="h-10 px-5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all hover:opacity-80"
                      style={{ background: "#22c55e", color: "#fff" }}>
                      <CheckCircle className="w-4 h-4" />
                      {t("Coupon.dashboard", lang)}
                    </Link>
                    <Link to="/shop"
                      className="h-10 px-5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all hover:opacity-80"
                      style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
                      {t("DashboardV2.go_to_shop", lang)}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
